import React, { useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { HelpCircle, ExternalLink } from "lucide-react";

/**
 * ContextualHelpButton — floating "?" pill in the bottom-right that
 * deep-links into the in-app User Guide (`/docs`) anchored at the
 * section relevant to the user's current route.
 *
 * Why this exists:
 *  - The guide has 13 H2 sections + ~25 H3s. Forcing users to scan
 *    the TOC every time they hit a confusing page wastes attention.
 *  - A help button that "just knows where I am" turns the docs into
 *    an in-context coach instead of a separate reference manual.
 *
 * The anchor slugs mirror Docs.jsx's slugger output exactly so a
 * navigation to `/docs#<slug>` reliably scrolls to the right heading
 * (Docs.jsx also re-applies the scroll after the markdown finishes
 * loading — see its useEffect on `md`).
 *
 * The button auto-hides on `/docs` itself (user is already there).
 */

// Route → section. Longest-prefix wins so `/migrate/storage` doesn't
// fall through to `/migrate` (which doesn't exist as a destination
// anyway, but we want explicit control over precedence).
const ROUTE_HELP = [
    [/^\/$/, { slug: "2-the-dashboard", label: "The Dashboard" }],
    [/^\/migrations(\/|$)/, { slug: "41-pc-migration", label: "PC Migration" }],
    [/^\/migrate\/server/, { slug: "42-server-migration", label: "Server Migration" }],
    [/^\/migrate\/storage/, { slug: "43-data-migration", label: "Data Migration" }],
    [/^\/migrate(\/|$)/, { slug: "4-migrations", label: "Migrations" }],
    [/^\/jobs(\/|$)/, { slug: "4-migrations", label: "Migrations" }],
    [/^\/agents(\/|$)/, { slug: "3-devices-agents", label: "Devices" }],
    [/^\/schedules(\/|$)/, { slug: "5-backups-schedules", label: "Backups" }],
    [/^\/analytics(\/|$)/, { slug: "2-the-dashboard", label: "Analytics" }],
    [/^\/alerts(\/|$)/, { slug: "6-alerts", label: "Alerts" }],
    [/^\/reports(\/|$)/, { slug: "7-reports", label: "Reports" }],
    [/^\/audit(\/|$)/, { slug: "8-audit-log", label: "Audit Log" }],
    [/^\/retention(\/|$)/, { slug: "9-retention", label: "Retention" }],
    [/^\/users(\/|$)/, { slug: "101-team--roles", label: "Team & Roles" }],
    [/^\/notifications(\/|$)/, { slug: "102-notifications", label: "Notifications" }],
    [/^\/sso-settings(\/|$)/, { slug: "103-sso--scim", label: "SSO & SCIM" }],
    [/^\/security(\/|$)/, { slug: "104-security-2fa--privacy", label: "Security & 2FA" }],
    [/^\/access-tokens(\/|$)/, { slug: "105-api-tokens", label: "API Tokens" }],
    [/^\/api-tokens(\/|$)/, { slug: "105-api-tokens", label: "API Tokens" }],
    [/^\/billing(\/|$)/, { slug: "106-billing", label: "Billing" }],
    [/^\/settings\/billing/, { slug: "106-billing", label: "Billing" }],
    [/^\/settings(\/|$)/, { slug: "10-settings", label: "Settings" }],
];

const DEFAULT_HELP = { slug: "1-getting-started", label: "Getting started" };

export default function ContextualHelpButton() {
    const { pathname } = useLocation();
    const [params] = useSearchParams();
    const [hover, setHover] = useState(false);

    // Hide on the User Guide itself + on unauthenticated pages.
    const hidden = useMemo(() => {
        if (pathname.startsWith("/docs")) return true;
        if (pathname.startsWith("/login") || pathname.startsWith("/register")) return true;
        if (pathname.startsWith("/superadmin/login")) return true;
        return false;
    }, [pathname]);

    const target = useMemo(() => {
        // /access-tokens hosts two tabs (Tokens vs Download Agent) behind
        // one pathname — the /^\/deploy-agent/ entry below only ever
        // matched the old standalone page, which now redirects here before
        // this component ever sees "/deploy-agent" in the URL.
        if (pathname.startsWith("/access-tokens") && params.get("tab") === "deploy-agent") {
            return { slug: "33-deploying-a-new-agent", label: "Download the agent" };
        }
        for (const [re, info] of ROUTE_HELP) {
            if (re.test(pathname)) return info;
        }
        return DEFAULT_HELP;
    }, [pathname, params]);

    if (hidden) return null;

    return (
        <div
            className="pointer-events-none fixed bottom-5 right-5 z-40 flex items-end gap-2"
            data-testid="contextual-help-root"
        >
            {hover && (
                <div
                    role="tooltip"
                    data-testid="contextual-help-tooltip"
                    className="pointer-events-none mb-2 hidden max-w-[240px] rounded-md border border-border bg-surface px-3 py-2 text-[12px] text-foreground shadow-lg sm:block"
                >
                    <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
                        Help for this page
                    </div>
                    <div className="mt-0.5 font-semibold">{target.label}</div>
                    <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                        <ExternalLink size={10} /> Opens the User Guide
                    </div>
                </div>
            )}
            <Link
                to={`/docs#${target.slug}`}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                onFocus={() => setHover(true)}
                onBlur={() => setHover(false)}
                aria-label={`Open user guide for ${target.label}`}
                data-testid="contextual-help-btn"
                className="pointer-events-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-[#5b5ef0] to-[#7c5cff] text-white shadow-lg shadow-[#5b5ef0]/40 ring-2 ring-white/10 transition-all hover:scale-105 hover:shadow-xl hover:shadow-[#5b5ef0]/55 focus:outline-none focus:ring-4 focus:ring-[#5b5ef0]/35"
            >
                <HelpCircle size={22} strokeWidth={2.2} />
            </Link>
        </div>
    );
}
